import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, signal, computed, inject, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpResponse } from '@angular/common/http';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { catchError, forkJoin, map, of } from 'rxjs';
import {
  DataTableComponent,
  PaginationEvent,
  TableColumn
} from '@shared/components/data-table/data-table.component';
import { OrderControllerService } from '@app/api/order-service/api/orderController.service';
import { ItemSearchDTO } from '@app/api/order-service/model/itemSearchDTO';
import { OrderItemPageVO } from '@app/api/order-service/model/orderItemPageVO';
import { OrderItemLinePageVO } from '@app/api/order-service/model/orderItemLinePageVO';
import { PageInfoOrderItemPageVO } from '@app/api/order-service/model/pageInfoOrderItemPageVO';
import { formatCurrencyVND } from '@app/shared/utils/date-formatter.util';
import { ORDER_ITEM_DIALOG_CONFIG } from '../../internal.config';

interface PackageChildRow {
  spuName: string;
  spuCode: string;
  skuName: string;
  skuCode: string;
  quantity: number;
}

interface OrderItemTableRow extends OrderItemPageVO {
  packageChildren?: PackageChildRow[];
}

@Component({
  selector: 'app-order-item-dialog',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    DialogModule,
    ButtonModule,
    TooltipModule,
    DataTableComponent
  ],
  templateUrl: './order-item-dialog.component.html',
  styleUrls: ['./order-item-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrderItemDialogComponent implements OnInit, OnChanges {
  @Input() visible = false;
  @Input() orderId: number | null = null;
  @Input() orderNumber: string | null = null;
  @Output() visibleChange = new EventEmitter<boolean>();

  private translateService = inject(TranslateService);
  private orderService = inject(OrderControllerService);

  // Dialog state
  loading = signal(false);
  orderItems = signal<OrderItemTableRow[]>([]);
  totalProducts = computed(() =>
    this.orderItems().reduce((total, item) => total + (item.quantity ?? 0), 0)
  );
  totalPayment = computed(() =>
    this.orderItems().reduce((total, item) => total + (item.innerSalePrice ?? 0), 0)
  );
  totalRecords = signal(0);
  currentPage = signal(1);
  pageSize = signal(20);
  private isLoadingItems = false;
  private packageLineLoadSequence = 0;
  config = ORDER_ITEM_DIALOG_CONFIG;
  dialogStyle = computed(() => ({
    width: this.config?.width,
    maxWidth: this.config?.maxWidth,
    height: this.config?.height,
    maxHeight: this.config?.maxHeight
  }));
  // Currency formatter
  formatCurrency = formatCurrencyVND;

  // Table columns configuration
  tableColumns: TableColumn[] = this.config.tableColumns;

  ngOnInit(): void {
    // Component initialization
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Only load items if dialog is visible and we have an orderId
    if (this.visible && this.orderId && !this.isLoadingItems) {
      this.loadOrderItems();
    }
  }

  onVisibleChange(visible: boolean): void {
    this.visibleChange.emit(visible);
    if (!visible) {
      this.resetDialog();
    }
  }

  private loadOrderItems(): void {
    if (!this.orderId || this.isLoadingItems) {
      return;
    }

    this.isLoadingItems = true;
    this.loading.set(true);

    const searchParams: ItemSearchDTO = {
      orderId: this.orderId,
      page: this.currentPage() - 1, // API uses 0-based indexing
      size: this.pageSize()
    };

    this.orderService.queryItemPage(searchParams, 'response', false, {
      httpHeaderAccept: 'application/json' as any
    }).subscribe({
      next: (response: HttpResponse<PageInfoOrderItemPageVO>) => {
        if (response?.body?.pageContent) {
          const orderItems: OrderItemTableRow[] = response.body.pageContent;
          this.orderItems.set(orderItems);
          this.totalRecords.set(response.body.totalElements || 0);
          this.loadPackageItemLines(orderItems);
        } else {
          this.orderItems.set([]);
          this.totalRecords.set(0);
        }
        this.loading.set(false);
        this.isLoadingItems = false;
      },
      error: (error) => {
        this.orderItems.set([]);
        this.totalRecords.set(0);
        this.loading.set(false);
        this.isLoadingItems = false;
      }
    });
  }

  private loadPackageItemLines(orderItems: OrderItemTableRow[]): void {
    const packageItems = orderItems.filter(
      (item): item is OrderItemTableRow & { id: number } =>
        Number(item.setFlag) === 1 && item.id !== undefined && item.id !== null,
    );

    if (packageItems.length === 0) {
      this.packageLineLoadSequence++;
      return;
    }

    const loadSequence = ++this.packageLineLoadSequence;

    forkJoin(
      packageItems.map((parentItem) =>
        this.orderService.queryItemLineList(parentItem.id, 'body', false, {
          httpHeaderAccept: 'application/json' as any
        }).pipe(
          map((lines) => ({ parentItem, lines: lines ?? [] })),
          catchError(() => of({ parentItem, lines: [] }))
        )
      )
    ).subscribe((groups) => {
      if (this.packageLineLoadSequence !== loadSequence) {
        return;
      }

      const childrenByParentId = new Map<number, PackageChildRow[]>();
      groups.forEach(({ parentItem, lines }) => {
        childrenByParentId.set(
          parentItem.id,
          lines.map((line) => this.toPackageChildRow(line)),
        );
      });

      this.orderItems.update((items) =>
        items.map((item) => {
          const packageChildren = item.id
            ? childrenByParentId.get(item.id)
            : undefined;
          return packageChildren?.length
            ? { ...item, packageChildren }
            : item;
        }),
      );
    });
  }

  private toPackageChildRow(line: OrderItemLinePageVO): PackageChildRow {
    return {
      spuName: line.spuName ?? '',
      spuCode: line.spuCode ?? '',
      skuName: line.skuName ?? line.spuName ?? '',
      skuCode: line.skuCode ?? '',
      quantity: line.quantity ?? 0,
    };
  }

  onPageChange(event: PaginationEvent): void {
    if (event.page === undefined) {
      return;
    }

    this.currentPage.set(event.page + 1);
    this.loadOrderItems();
  }

  onClose(): void {
    this.onVisibleChange(false);
  }

  private resetDialog(): void {
    this.orderItems.set([]);
    this.packageLineLoadSequence++;
    this.totalRecords.set(0);
    this.currentPage.set(1);
    this.isLoadingItems = false;
  }
}
